import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Star, TrendingUp, X, Plus } from "lucide-react";

const BecomeTipster = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [selectedSports, setSelectedSports] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    payout_email: user?.email || "",
    subscription_price: "",
    bio: ""
  });

  const availableSports = ["Football", "Basketball", "Tennis", "Baseball", "Hockey", "Cricket", "Rugby", "Golf"];

  const handleSportToggle = (sport: string) => {
    setSelectedSports(prev => 
      prev.includes(sport) 
        ? prev.filter(s => s !== sport)
        : [...prev, sport]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to become a tipster",
        variant: "destructive"
      });
      return;
    }

    if (selectedSports.length === 0) {
      toast({
        title: "Select sports",
        description: "Please select at least one sport you'll provide tips for",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-tipster-profile', {
        body: {
          sports_tags: selectedSports,
          payout_email: formData.payout_email,
          subscription_price: formData.subscription_price ? parseFloat(formData.subscription_price) : null
        }
      });

      if (error) throw error;

      // Update profile bio if provided
      if (formData.bio) {
        await supabase
          .from('profiles')
          .update({ bio: formData.bio })
          .eq('user_id', user.id);
      }

      toast({
        title: "Welcome to BetVerse!",
        description: "Your tipster profile has been created successfully."
      });

      navigate('/create-tip');
    } catch (error: any) {
      toast({
        title: "Error creating tipster profile",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold text-foreground mb-4">Authentication Required</h1>
          <p className="text-muted-foreground mb-8">Please sign in to become a tipster</p>
          <Button onClick={() => navigate('/auth')}>Sign In</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-foreground mb-4">
            Become a <span className="bg-gradient-hero bg-clip-text text-transparent">Professional Tipster</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Share your betting expertise, build a following, and earn money from your successful predictions
          </p>
        </div>

        {/* Benefits */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <Card className="bg-gradient-card border-border/50 text-center">
            <CardContent className="p-6">
              <div className="bg-gradient-profit p-3 rounded-lg w-fit mx-auto mb-4">
                <TrendingUp className="h-6 w-6 text-profit-foreground" />
              </div>
              <h2 className="font-semibold text-foreground mb-2 text-base">Earn Revenue</h2>
              <p className="text-sm text-muted-foreground">Set your own prices and earn from tip sales</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-card border-border/50 text-center">
            <CardContent className="p-6">
              <div className="bg-gradient-premium p-3 rounded-lg w-fit mx-auto mb-4">
                <Star className="h-6 w-6 text-premium-foreground" />
              </div>
              <h2 className="font-semibold text-foreground mb-2 text-base">Build Reputation</h2>
              <p className="text-sm text-muted-foreground">Gain credibility with verified performance tracking</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-card border-border/50 text-center">
            <CardContent className="p-6">
              <div className="bg-gradient-hero p-3 rounded-lg w-fit mx-auto mb-4">
                <Star className="h-6 w-6 text-primary-foreground" />
              </div>
              <h2 className="font-semibold text-foreground mb-2 text-base">Global Reach</h2>
              <p className="text-sm text-muted-foreground">Connect with bettors from around the world</p>
            </CardContent>
          </Card>
        </div>

        {/* Application Form */}
        <Card className="bg-gradient-card border-border/50">
          <CardHeader>
            <CardTitle className="text-foreground">Tipster Application</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Sports Selection */}
              <div>
                <Label className="text-base font-medium mb-4 block">Sports You'll Cover *</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {availableSports.map(sport => (
                    <div
                      key={sport}
                      onClick={() => handleSportToggle(sport)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedSports.includes(sport)
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{sport}</span>
                        {selectedSports.includes(sport) && (
                          <Plus className="h-4 w-4 rotate-45" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {selectedSports.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {selectedSports.map(sport => (
                      <Badge key={sport} variant="secondary" className="flex items-center gap-1">
                        {sport}
                        <X 
                          className="h-3 w-3 cursor-pointer hover:text-destructive" 
                          onClick={() => handleSportToggle(sport)}
                        />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Bio */}
              <div>
                <Label htmlFor="bio">About You</Label>
                <Textarea
                  id="bio"
                  value={formData.bio}
                  onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                  placeholder="Tell potential followers about your expertise, experience, and approach to betting..."
                  rows={4}
                />
              </div>

              {/* Payout Email */}
              <div>
                <Label htmlFor="payout_email">Payout Email *</Label>
                <Input
                  id="payout_email"
                  type="email"
                  value={formData.payout_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, payout_email: e.target.value }))}
                  placeholder="your-email@example.com"
                  required
                />
                <p className="text-sm text-muted-foreground mt-1">
                  Where you'd like to receive payouts from tip sales
                </p>
              </div>

              {/* Subscription Price */}
              <div>
                <Label htmlFor="subscription_price">Monthly Subscription Price (Optional)</Label>
                <Input
                  id="subscription_price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.subscription_price}
                  onChange={(e) => setFormData(prev => ({ ...prev, subscription_price: e.target.value }))}
                  placeholder="29.99"
                />
                <p className="text-sm text-muted-foreground mt-1">
                  Set a monthly subscription price for exclusive access to your tips
                </p>
              </div>

              {/* Terms */}
              <div className="bg-muted/20 p-4 rounded-lg">
                <h3 className="font-medium text-foreground mb-2">Agreement</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• I understand that all my performance data will be publicly tracked</li>
                  <li>• I agree to provide honest and accurate betting insights</li>
                  <li>• I will not engage in misleading or fraudulent activities</li>
                  <li>• I understand that BetVerse takes a commission from tip sales</li>
                </ul>
              </div>

              {/* Submit */}
              <Button 
                type="submit" 
                className="w-full" 
                size="lg"
                disabled={loading}
                variant="betting"
              >
                {loading ? "Creating Profile..." : "Create Tipster Profile"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BecomeTipster;